import { yupResolver } from '@hookform/resolvers/yup';
import { FormFieldError } from '@kira/checkpoint-shared-frontend';
import { useQueryClient } from '@tanstack/react-query';
import React, { useState, useEffect, useMemo } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import Skeleton from 'react-loading-skeleton';
import { twMerge } from 'tailwind-merge';
import * as yup from 'yup';

import CTAModal from 'components/CTAModal/CTAModal';
import CounterInput from 'components/CounterInput/CounterInput';
import DatePickerInput from 'components/DatePickerInput/DatePickerInput';
import FormControls from 'components/FormControls/FormControls';
import InlineForm from 'components/InlineForm/InlineForm';
import Panel from 'components/Panel/Panel';
import SelectMenu from 'components/SelectMenu/SelectMenu';
import ToggleSwitch from 'components/ToggleSwitch/ToggleSwitch';
import { PeriodType, AssessmentSettings } from 'hooks/useGetAssessmentSettings';
import useNotification from 'hooks/useNotification';
import useUpdateAssessmentDeadline from 'hooks/useUpdateAssessmentDeadline';
import { t } from 'locales/i18n';
import { formatDateForTimezone } from 'utils/formatDate';
import { timeZoneOffset } from 'utils/timeZoneOffset';

interface DeadlinesProps {
  interviewUid: string;
  data?: AssessmentSettings;
  isLoading: boolean;
}

enum FormFields {
  START_DATE = 'startDate',
  CLOSED_DATE = 'closedDate',
  TIME_ZONE = 'timeZone',
  ENABLE = 'enable',
  INTERVAL = 'interval',
  INTERVAL_PERIOD = 'intervalPeriod',
}
interface FormData {
  [FormFields.START_DATE]: string;
  [FormFields.CLOSED_DATE]: string;
  [FormFields.TIME_ZONE]: string;
  [FormFields.ENABLE]: boolean;
  [FormFields.INTERVAL]: number;
  [FormFields.INTERVAL_PERIOD]: string;
}

const timeZones = Intl.supportedValuesOf('timeZone').map((timeZone) => {
  const offset = new Intl.DateTimeFormat('en', {
    timeZone,
    timeZoneName: 'shortOffset',
  })
    .formatToParts()
    .find((part) => part.type === 'timeZoneName');
  const timeZoneAbbrivation = new Intl.DateTimeFormat('en', {
    timeZone,
    timeZoneName: 'long',
  })
    .formatToParts()
    .find((part) => part.type === 'timeZoneName');
  return offset && timeZoneAbbrivation
    ? {
        value: timeZone,
        primaryText: `${timeZone} - ${timeZoneAbbrivation.value} (${offset.value})`,
      }
    : { value: timeZone, primaryText: timeZone };
});

const Deadlines = ({ data, interviewUid, isLoading }: DeadlinesProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { showErrorNotification, showSuccessNotification } = useNotification();

  const {
    updateAssessmentDeadline,
    isLoading: isLoadingUpdateAssessmentSettings,
  } = useUpdateAssessmentDeadline();

  const hasInvitedApplicants = !!data?.number_of_invited_applicants;
  const queryClient = useQueryClient();

  const defaultFormValues = useMemo(() => {
    const timeZone =
      data?.interview_time_zone ??
      Intl.DateTimeFormat().resolvedOptions().timeZone;
    return {
      [FormFields.START_DATE]: data?.start_date
        ? formatDateForTimezone(data.start_date, timeZone)
        : '',
      [FormFields.CLOSED_DATE]: data?.closed_date
        ? formatDateForTimezone(data.closed_date, timeZone)
        : '',
      [FormFields.TIME_ZONE]: timeZone,
      [FormFields.ENABLE]: data?.has_rolling_applicant_deadline ?? false,
      [FormFields.INTERVAL]: data?.rolling_applicant_deadline ?? 1,
      [FormFields.INTERVAL_PERIOD]:
        data?.rolling_applicant_deadline_period ?? PeriodType.DAY,
    };
  }, [JSON.stringify(data)]);

  useEffect(() => {
    reset(defaultFormValues);
  }, [data]);

  const getIntervalDays = (interval: number, period: string) => {
    if (period === PeriodType.WEEK) {
      return interval * 7;
    }

    if (period === PeriodType.MONTH) {
      return interval * 30;
    }

    return interval;
  };

  const FormSchema = useMemo(
    () =>
      yup.object().shape({
        [FormFields.START_DATE]: yup
          .string()
          .required(t('Open date is required'))
          .typeError(t('Open date is required')),
        [FormFields.CLOSED_DATE]: yup
          .string()
          .nullable()
          .test(
            FormFields.START_DATE,
            t('The start date has to be before the closed date.'),
            (closeDate) => {
              if (!closeDate) {
                return true;
              }

              const startDate = watch(FormFields.START_DATE);
              if (new Date(closeDate) <= new Date(startDate)) {
                return false;
              }

              return true;
            }
          ),
        [FormFields.TIME_ZONE]: yup
          .string()
          .required(t('Time zone is required')),
        [FormFields.INTERVAL]: yup
          .number()
          .min(1, t('Deadline interval must be an integer between 1 and 180'))
          .test(
            'deadline_close_date_validation',
            t(
              'Applicant deadlines cannot be later than interview’s close date'
            ),
            (value) => {
              const intervalPeriod = watch(FormFields.INTERVAL_PERIOD);
              const closeDate = watch(FormFields.CLOSED_DATE);

              if (value && closeDate) {
                const intervalDays = getIntervalDays(value, intervalPeriod);
                const diffTime = Math.abs(
                  new Date(closeDate).getTime() - new Date().getTime()
                );
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                if (intervalDays > diffDays) {
                  return false;
                }
              }

              return true;
            }
          )
          .test(
            'deadline_max_days_validation',
            t('Deadline interval cannot exceed 180 days'),
            (value) => {
              if (!value) {
                return true;
              }

              const intervalPeriod = watch(FormFields.INTERVAL_PERIOD);
              const intervalDays = getIntervalDays(value, intervalPeriod);

              if (intervalDays > 180) {
                return false;
              }

              return true;
            }
          )
          .required(t('Deadline interval is required')),
      }),
    [
      FormFields.START_DATE,
      FormFields.CLOSED_DATE,
      FormFields.TIME_ZONE,
      FormFields.INTERVAL,
      FormFields.INTERVAL_PERIOD,
    ]
  );

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    reset,
  } = useForm<FormData>({
    resolver: yupResolver(FormSchema),
    mode: 'onSubmit',
    defaultValues: defaultFormValues,
  });

  const localTimeZone = timeZones.find(
    (option) => option.value === watch(FormFields.TIME_ZONE)
  )?.primaryText;

  const handleConfirmation = (e: React.FormEvent) => {
    setIsModalOpen(true);
    e.preventDefault();
  };

  const onSubmit: SubmitHandler<FormData> = async ({
    startDate,
    closedDate,
    enable,
    interval,
    intervalPeriod,
    timeZone,
  }) => {
    // Format dates to an ISO string with the specified interview time zone offset
    const startDateFormatted =
      new Date(startDate).toLocaleString('sv-SE').replace(' ', 'T') +
      timeZoneOffset(timeZone, new Date(startDate));
    const closedDateFormatted = closedDate
      ? new Date(closedDate).toLocaleString('sv-SE').replace(' ', 'T') +
        timeZoneOffset(timeZone, new Date(closedDate))
      : null;

    await updateAssessmentDeadline(
      {
        interviewUid,
        startDate: startDateFormatted,
        closedDate: closedDateFormatted,
        isEnable: enable,
        interval: getIntervalDays(interval, intervalPeriod),
        timeZone,
      },
      {
        onSuccess: () => {
          showSuccessNotification(
            t('Assessment settings saved'),
            t('Your settings were successfully saved')
          );
          queryClient.invalidateQueries({
            queryKey: ['getAssessmentSettings', interviewUid],
          });
          queryClient.resetQueries({
            queryKey: ['getAssessmentEmailSettings', interviewUid],
          });
        },
        onError: (error) => {
          showErrorNotification(
            t('An error occurred while updating assessment'),
            error.message
          );
        },
      }
    );
  };

  const deadlineFieldsClassName = twMerge(
    'flex',
    'w-ful',
    'transition-all',
    watch(FormFields.ENABLE) ? 'h-auto' : 'h-0',
    watch(FormFields.ENABLE) ? '' : '!mt-0',
    watch(FormFields.ENABLE) ? '' : 'overflow-hidden'
  );

  const intervalPeriods = [
    PeriodType.DAY,
    PeriodType.WEEK,
    PeriodType.MONTH,
  ].map((item) => ({
    value: item,
    primaryText: watch(FormFields.INTERVAL) > 1 ? `${item}s` : item,
  }));

  const hasDisableConfirmation =
    data?.has_rolling_applicant_deadline &&
    watch(FormFields.ENABLE) === false &&
    hasInvitedApplicants;

  return isLoading ? (
    <Skeleton height={250} />
  ) : (
    <form
      data-testid="student-id-requirement-form"
      onSubmit={
        hasDisableConfirmation ? handleConfirmation : handleSubmit(onSubmit)
      }
    >
      <fieldset>
        <Panel
          title={t('Deadlines')}
          isFocused={isDirty}
          focusedClassName="shadow-md"
        >
          <InlineForm
            title={t('Open date')}
            description={t(
              'Determine when the assessment is available to applicants. Before the open date, applicants can still complete their other Check In steps, including Registration and Practice.'
            )}
          >
            <div className="flex items-center lg:pl-12">
              <Controller
                control={control}
                name={FormFields.START_DATE}
                render={({ field: { onChange, value } }) => (
                  <DatePickerInput
                    value={value}
                    id="startDate"
                    onChange={(newDate) => onChange(newDate)}
                  />
                )}
              />
              <p className="prose-body-sm-reg pl-6 text-kira-slate-700">
                {localTimeZone}
              </p>
              <FormFieldError
                isVisible={!!errors[FormFields.START_DATE]}
                message={errors[FormFields.START_DATE]?.message ?? ''}
                className="mt-2"
              />
            </div>
          </InlineForm>
          <InlineForm
            title={t('Close date')}
            description={t(
              'Set the date after which the assessment cannot be completed by applicants but remains accessible for reviewing activities.'
            )}
          >
            <div className="flex items-center lg:pl-12">
              <Controller
                control={control}
                name={FormFields.CLOSED_DATE}
                render={({ field: { onChange, value } }) => (
                  <DatePickerInput
                    value={value}
                    id="closeDate"
                    onChange={(newDate) => onChange(newDate)}
                  />
                )}
              />
              <p className="prose-body-sm-reg pl-6 text-kira-slate-700">
                {localTimeZone}
              </p>
              <FormFieldError
                isVisible={!!errors[FormFields.CLOSED_DATE]}
                message={errors[FormFields.CLOSED_DATE]?.message ?? ''}
                className="mt-2"
              />
            </div>
          </InlineForm>
          <InlineForm
            title={t('Rolling applicant deadlines')}
            description={t(
              'Enable individual submission deadlines for each applicant.'
            )}
            childrenClassNames="lg:pl-12"
          >
            <div className="lg:pl-12">
              <Controller
                name={FormFields.ENABLE}
                control={control}
                render={({ field: { onChange, value } }) => (
                  <ToggleSwitch enabled={value} setEnabled={onChange} />
                )}
              />
            </div>
          </InlineForm>
          <div className={deadlineFieldsClassName}>
            <div className="w-full space-y-6 border-l-4 border-kira-slate-200 pl-4">
              <InlineForm
                title={t('Deadline interval')}
                description={t(
                  'Set how long an applicant has to complete their assessment after invitation. All deadlines will be set to 11:59 PM in the interview’s time zone.'
                )}
                childrenClassNames="lg:pl-8"
              >
                <div className="grid grid-cols-2 gap-4 lg:pl-12">
                  <div className="lg:w-96">
                    <Controller
                      name={FormFields.INTERVAL}
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <div className="mt-2">
                          <CounterInput
                            isIntegerOnly
                            inputClassName="w-[250px]"
                            value={value}
                            setValue={onChange}
                          />
                        </div>
                      )}
                    />
                  </div>

                  <div className="mb-2">
                    <Controller
                      control={control}
                      name={FormFields.INTERVAL_PERIOD}
                      render={({ field }) => (
                        <SelectMenu
                          width={250}
                          options={intervalPeriods}
                          value={intervalPeriods.find(
                            (option) => option.value === field.value
                          )}
                          onChange={(option) => {
                            field.onChange(option.value);
                          }}
                        />
                      )}
                    />
                  </div>
                </div>

                <FormFieldError
                  isVisible={!!errors[FormFields.INTERVAL]}
                  message={errors[FormFields.INTERVAL]?.message ?? ''}
                  className="mt-2 lg:pl-12"
                />
                <FormFieldError
                  isVisible={!!errors[FormFields.INTERVAL_PERIOD]}
                  message={errors[FormFields.INTERVAL_PERIOD]?.message ?? ''}
                  className="mt-2"
                />
              </InlineForm>
              <InlineForm
                title={t('Time zone')}
                description={t(
                  'Set the time zone for applicant deadline dates communicated in invitation and reminder emails.'
                )}
                childrenClassNames="lg:pl-8"
              >
                <div className="lg:pl-12">
                  <Controller
                    control={control}
                    name={FormFields.TIME_ZONE}
                    render={({ field }) => (
                      <SelectMenu
                        width={250}
                        options={timeZones}
                        emptyText={t('Select time zone')}
                        withSearch
                        value={timeZones.find(
                          (option) => option.value === field.value
                        )}
                        onChange={(option) => {
                          field.onChange(option.value);
                        }}
                      />
                    )}
                  />
                  <FormFieldError
                    isVisible={!!errors[FormFields.TIME_ZONE]}
                    message={errors[FormFields.TIME_ZONE]?.message ?? ''}
                    className="mt-2"
                  />
                </div>
              </InlineForm>
            </div>
          </div>
          {isDirty && (
            <FormControls
              isLoading={isLoadingUpdateAssessmentSettings}
              onCancel={() => reset(defaultFormValues)}
            />
          )}
        </Panel>
      </fieldset>
      <CTAModal
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        title={t('Disable Rolling Deadlines')}
        description={t(
          'Disabling rolling deadlines will delete deadlines for all applicants.'
        )}
        confirmation={t('Are you sure you want to disable it?')}
        action={async () => {
          await handleSubmit(onSubmit)();
          setIsModalOpen(false);
        }}
        isLoading={isLoadingUpdateAssessmentSettings}
        primaryButtonText={t('Disable')}
      />
    </form>
  );
};

export default Deadlines;
